import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalysisApiClientAccess } from '@nk/access-block';
import { CachingService, StableCache } from '@nk/caching-lib';
import { EntityUpdateTimestampService } from '@nk/core/services/entity-update-timestamp.service';
import {
  AppFeatureKey,
  CompanyLocationEntity,
  CompanyLocationTypeEntity,
  COUNTRY_FEATURES,
  GeoCountryEntity,
  GeoLocationCompositeEntity,
  GeoLocationEntity,
  GeoMunicipalityEntity,
  GeoPostalCodeEntity,
  GeoSubdivisionEntity,
  SimpleCollection,
} from '@nk/data-entities-lib';
import { Observable } from 'rxjs';

@Injectable()
export class GeoService {
  constructor(
    private _apiAnalysisApiAccess: AnalysisApiClientAccess,
    private _cachingService: CachingService,
    private _entityUpdateTimestampService: EntityUpdateTimestampService,
  ) {}

  public geoCountryListCache = new StableCache(
    'app.geo_country',
    this,
    this.fetchGeoCountryList,
    this._entityUpdateTimestampService.timestamps$,
  );
  public fetchGeoCountryList(): Observable<GeoCountryEntity[]> {
    return this._apiAnalysisApiAccess.get<GeoCountryEntity[]>('geo-country');
  }

  public geoSubdivisionListCache = new StableCache(
    'app.geo_subdivision',
    this,
    this.fetchGeoSubdivisionList,
    this._entityUpdateTimestampService.timestamps$,
  );
  public fetchGeoSubdivisionList(geoCountryIdentifiers: string = null): Observable<GeoSubdivisionEntity[]> {
    let httpParams = new HttpParams();
    // null defaults to all countries
    httpParams = httpParams.append('geo_country_identifiers', String(geoCountryIdentifiers));
    return this._apiAnalysisApiAccess.get<GeoSubdivisionEntity[]>('geo-subdivision', { params: httpParams });
  }

  public geoMunicipalityListCache = new StableCache(
    'app.geo_municipality',
    this,
    this.fetchGeoMunicipalityList,
    this._entityUpdateTimestampService.timestamps$,
  );
  public fetchGeoMunicipalityList(geoCountryIdentifiers: string = null): Observable<GeoMunicipalityEntity[]> {
    let httpParams = new HttpParams();
    // null defaults to all countries
    httpParams = httpParams.append('geo_country_identifiers', String(geoCountryIdentifiers));
    return this._apiAnalysisApiAccess.get<GeoMunicipalityEntity[]>('geo-municipality', { params: httpParams });
  }

  public geoPostalCodeListCache = new StableCache(
    'app.geo_postal_code',
    this,
    this.fetchGeoPostalCodeList,
    this._entityUpdateTimestampService.timestamps$,
  );
  public geoPostalCodesByUuidsCache = this._cachingService.create(this, this.fetchGeoPostalCodeList);
  /**
   * @param geoCountryIdentifiers - NO,DK,SE or null. null is default to all countries
   * @param geoPostalCodeUuids
   */
  public fetchGeoPostalCodeList(
    geoCountryIdentifiers: string = null,
    geoPostalCodeUuids?: string[],
  ): Observable<GeoPostalCodeEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('geo_country_identifiers', String(geoCountryIdentifiers));
    if (geoPostalCodeUuids?.length) {
      httpParams = httpParams.append('geo_postal_code_identifiers', geoPostalCodeUuids.join(','));
    }
    return this._apiAnalysisApiAccess.get<GeoPostalCodeEntity[]>('geo-postal-code', { params: httpParams });
  }

  public companyLocationTypeCache = new StableCache(
    'app.company_location_type',
    this,
    this.fetchCompanyLocationType,
    this._entityUpdateTimestampService.timestamps$,
  );
  public fetchCompanyLocationType(): Observable<CompanyLocationTypeEntity[]> {
    return this._apiAnalysisApiAccess.get<CompanyLocationTypeEntity[]>('company-location-type');
  }

  public companyLocationCache = this._cachingService.create(this, this.fetchCompanyLocation);
  public fetchCompanyLocation(companyUuid: string): Observable<CompanyLocationEntity[]> {
    return this._apiAnalysisApiAccess.get<CompanyLocationEntity[]>(`company/${companyUuid}/company-location`, {}, [
      AppFeatureKey.base,
      ...COUNTRY_FEATURES,
    ]);
  }

  public geoLocationChildrenCache = this._cachingService.create(this, this.fetchGeoLocationChildren);
  public fetchGeoLocationChildren(primaryGeoLocationUuid: string): Observable<GeoLocationEntity[]> {
    let httpParams = new HttpParams();
    if (primaryGeoLocationUuid) {
      httpParams = httpParams.append('primary_geo_location_uuid', primaryGeoLocationUuid);
    }
    return this._apiAnalysisApiAccess.get<GeoLocationEntity[]>(`geo-location`, {
      params: httpParams,
    });
  }

  public geoLocationCompositeCache = this._cachingService.create(this, this.fetchGeoLocationComposite);
  public fetchGeoLocationComposite(geoLocationUuid: string): Observable<GeoLocationCompositeEntity> {
    return this._apiAnalysisApiAccess.get<GeoLocationCompositeEntity>(`geo-location-composite/${geoLocationUuid}`);
  }

  public geoLocationCompositeCollectionCache = this._cachingService.create(
    this,
    this.fetchGeoLocationCompositeCollection,
  );
  public fetchGeoLocationCompositeCollection(
    addressSearchQuery: string | undefined = undefined,
    postalCode: string | undefined = undefined,
    postalPlace: string | undefined = undefined,
    limit: number | undefined = undefined,
    offset: number | undefined = undefined,
  ): Observable<SimpleCollection<GeoLocationCompositeEntity>> {
    let httpParams = new HttpParams();
    if (addressSearchQuery?.trim()) {
      httpParams = httpParams.append('address_search_query', addressSearchQuery.trim());
    }
    if (postalCode?.trim()) {
      httpParams = httpParams.append('postal_code', postalCode.trim());
    }
    if (postalPlace?.trim()) {
      httpParams = httpParams.append('postal_place', postalPlace.trim());
    }
    if (typeof limit === 'number') {
      httpParams = httpParams.append('limit', limit);
    }
    if (typeof offset === 'number') {
      httpParams = httpParams.append('offset', offset);
    }
    return this._apiAnalysisApiAccess.get<SimpleCollection<GeoLocationCompositeEntity>>(
      `geo-location-composite-collection`,
      {
        params: httpParams,
      },
    );
  }
}
